import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface EmpresaFormData {
  codigo_empresa?: string;
  cnpj: string;
  razao_social: string;
  apelido?: string;
  inscricao_estadual?: string;
  inscricao_municipal?: string;
  email_nfe?: string;
  fora_municipio: boolean;
  ibge_municipio?: string;
  limite_credito?: number;
  matricula_cei_cno?: string;
  agencia?: string;
  conta?: string;
  pix?: string;
  banco?: string;
  cnpj_bancario?: string;
  ativo: boolean;
}

interface Local {
  id: string;
  nome: string;
  logradouro?: string;
  cidade?: string;
  estado?: string;
}

interface Contato {
  id: string;
  nome: string;
  email?: string;
  telefone?: string;
  cargo?: string;
}

interface Cliente {
  id: string;
  nome: string;
}

interface TipoLocal {
  id: string;
  nome: string;
}

export default function EmpresaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [locais, setLocais] = useState<Local[]>([]);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [tiposLocal, setTiposLocal] = useState<TipoLocal[]>([]);
  const [selectedLocais, setSelectedLocais] = useState<string[]>([]);
  const [selectedContatos, setSelectedContatos] = useState<string[]>([]);
  const [isContatoModalOpen, setIsContatoModalOpen] = useState(false);
  const [isLocalModalOpen, setIsLocalModalOpen] = useState(false);
  const [contatoLoading, setContatoLoading] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  
  const [contatoFormData, setContatoFormData] = useState({
    nome: "",
    email: "",
    telefone: "",
    cargo: "",
    observacoes: "",
    cliente_id: "none",
    local_id: "",
    ativo: true
  });

  const [localFormData, setLocalFormData] = useState({
    nome: "",
    descricao: "",
    tipo_local_id: "",
    cliente_id: "none",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    frete_moto: "",
    frete_carro_hatch: "",
    frete_utilitario: ""
  });
  
  const [formData, setFormData] = useState<EmpresaFormData>({
    cnpj: "",
    razao_social: "",
    apelido: "",
    inscricao_estadual: "",
    inscricao_municipal: "",
    email_nfe: "",
    fora_municipio: false,
    ibge_municipio: "",
    limite_credito: 0,
    matricula_cei_cno: "",
    agencia: "",
    conta: "",
    pix: "",
    banco: "",
    cnpj_bancario: "",
    ativo: true,
  });

  useEffect(() => {
    loadLocais();
    loadContatos();
    loadClientes();
    loadTiposLocal();
    if (isEdit) {
      loadEmpresa();
    }
  }, [id, isEdit]);

  const loadEmpresa = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("empresas")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      setFormData(data as any);

      // Carregar locais associados
      const { data: locaisData } = await supabase
        .from("locais")
        .select("id")
        .eq("id_empresa", id);
      
      if (locaisData) {
        setSelectedLocais(locaisData.map(l => l.id));
      }

      // Carregar contatos associados
      const { data: contatosData } = await supabase
        .from("contatos")
        .select("id")
        .eq("id_empresa", id);
      
      if (contatosData) {
        setSelectedContatos(contatosData.map(c => c.id));
      }

    } catch (error) {
      console.error("Erro ao carregar empresa:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados da empresa",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadLocais = async () => {
    try {
      // @ts-ignore - TypeScript type inference issue with Supabase
      const result: any = await supabase
        .from("locais")
        .select("id, nome, logradouro, cidade, estado")
        .eq("ativo", true)
        .order("nome");
      
      const { data, error } = result;

      if (error) throw error;
      setLocais(data || []);
    } catch (error) {
      console.error("Erro ao carregar locais:", error);
    }
  };

  const loadContatos = async () => {
    try {
      // @ts-ignore - Temporary fix for Supabase type inference issue
      let query: any = supabase
        .from("contatos")
        .select("id, nome, email, telefone, cargo")
        .eq("ativo", true);

      // Se estamos editando uma empresa existente, filtra apenas contatos dessa empresa
      if (isEdit && id) {
        query = query.eq("id_empresa", id);
      }
      // Se estamos criando uma nova empresa, mostra todos os contatos disponíveis

      const { data, error } = await query.order("nome");

      if (error) throw error;
      setContatos(data || []);
    } catch (error) {
      console.error("Erro ao carregar contatos:", error);
    }
  };

  const loadClientes = async () => {
    try {
      // @ts-ignore - TypeScript type inference issue with Supabase
      const result: any = await supabase
        .from("clientes")
        .select("id, nome")
        .eq("ativo", true)
        .order("nome");
      
      const { data, error } = result;

      if (error) throw error;
      setClientes(data || []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
    }
  };

  const loadTiposLocal = async () => {
    try {
      // @ts-ignore - Temporary fix for deep recursion type error
      const { data, error } = await supabase
        .from("tipo_local")
        .select("id, nome")
        .eq("ativo", true)
        .order("nome");

      if (error) throw error;
      setTiposLocal(data || []);
    } catch (error) {
      console.error("Erro ao carregar tipos de local:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.cnpj || !formData.razao_social) {
      toast({
        title: "Erro",
        description: "CNPJ e Razão Social são obrigatórios",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const dataToSave = {
        ...formData,
        updated_by: user?.id,
        ...(isEdit ? {} : { created_by: user?.id }),
      };

      let result;
      if (isEdit) {
        result = await supabase
          .from("empresas")
          .update(dataToSave)
          .eq("id", id)
          .select()
          .single();
      } else {
        // @ts-ignore - Schema mismatch temporary fix
        result = await supabase
          .from("empresas")
          .insert({
            ...dataToSave,
            nome: dataToSave.razao_social // Mapear razao_social para nome
          })
          .select()
          .single();
      }

      if (result.error) throw result.error;

      const empresaId = result.data.id;

      // Atualizar relacionamentos com locais
      if (selectedLocais.length > 0) {
        await supabase
          .from("locais")
          .update({ id_empresa: empresaId })
          .in("id", selectedLocais);
      }

      // Atualizar relacionamentos com contatos
      if (selectedContatos.length > 0) {
        await supabase
          .from("contatos")
          .update({ id_empresa: empresaId })
          .in("id", selectedContatos);
      }

      toast({
        title: "Sucesso",
        description: `Empresa ${isEdit ? "atualizada" : "criada"} com sucesso`,
      });

      navigate("/intranet/empresas");
    } catch (error) {
      console.error("Erro ao salvar empresa:", error);
      toast({
        title: "Erro",
        description: "Erro ao salvar empresa",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetContatoForm = () => {
    setContatoFormData({
      nome: "",
      email: "",
      telefone: "",
      cargo: "",
      observacoes: "",
      cliente_id: "none",
      local_id: "",
      ativo: true
    });
  };

  const resetLocalForm = () => {
    setLocalFormData({
      nome: "",
      descricao: "",
      tipo_local_id: "",
      cliente_id: "none",
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      frete_moto: "",
      frete_carro_hatch: "",
      frete_utilitario: ""
    });
  };

  const handleLocalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!localFormData.nome || !localFormData.tipo_local_id) {
      toast({
        title: "Erro",
        description: "Nome e Tipo do Local são obrigatórios",
        variant: "destructive",
      });
      return;
    }

    try {
      setLocalLoading(true);
      
      const dataToSubmit = {
        nome: localFormData.nome,
        descricao: localFormData.descricao || null,
        tipo_local_id: localFormData.tipo_local_id,
        cliente_id: localFormData.cliente_id === "none" ? null : localFormData.cliente_id,
        cep: localFormData.cep || null,
        logradouro: localFormData.logradouro || null,
        numero: localFormData.numero || null,
        complemento: localFormData.complemento || null,
        bairro: localFormData.bairro || null,
        cidade: localFormData.cidade || null,
        estado: localFormData.estado || null,
        frete_moto: localFormData.frete_moto ? parseFloat(localFormData.frete_moto) : null,
        frete_carro_hatch: localFormData.frete_carro_hatch ? parseFloat(localFormData.frete_carro_hatch) : null,
        frete_utilitario: localFormData.frete_utilitario ? parseFloat(localFormData.frete_utilitario) : null,
        ativo: true,
        created_by: user?.id,
        updated_by: user?.id
      };

      const { data, error } = await supabase
        .from("locais")
        .insert([dataToSubmit])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Local criado com sucesso",
      });

      // Recarregar lista de locais
      await loadLocais();
      
      // Fechar modal e limpar formulário
      setIsLocalModalOpen(false);
      resetLocalForm();

      // Auto-selecionar o local recém-criado
      if (data) {
        setSelectedLocais(prev => [...prev, data.id]);
      }

    } catch (error) {
      console.error("Erro ao salvar local:", error);
      toast({
        title: "Erro",
        description: "Erro ao salvar local",
        variant: "destructive",
      });
    } finally {
      setLocalLoading(false);
    }
  };

  const handleContatoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contatoFormData.nome) {
      toast({
        title: "Erro",
        description: "O nome é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      setContatoLoading(true);
      
      const dataToSubmit = {
        nome: contatoFormData.nome,
        email: contatoFormData.email || null,
        telefone: contatoFormData.telefone || null,
        cargo: contatoFormData.cargo || null,
        observacoes: contatoFormData.observacoes || null,
        cliente_id: contatoFormData.cliente_id === "none" ? null : contatoFormData.cliente_id,
        local_id: contatoFormData.local_id || null,
        ativo: contatoFormData.ativo,
        created_by: user?.id,
        updated_by: user?.id
      };

      const { data, error } = await supabase
        .from("contatos")
        .insert([dataToSubmit])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Contato criado com sucesso",
      });

      // Recarregar lista de contatos
      await loadContatos();
      
      // Fechar modal e limpar formulário
      setIsContatoModalOpen(false);
      resetContatoForm();

      // Auto-selecionar o contato recém-criado
      if (data) {
        setSelectedContatos(prev => [...prev, data.id]);
      }

    } catch (error) {
      console.error("Erro ao salvar contato:", error);
      toast({
        title: "Erro",
        description: "Erro ao salvar contato",
        variant: "destructive",
      });
    } finally {
      setContatoLoading(false);
    }
  };

  const handleInputChange = (field: keyof EmpresaFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/intranet/empresas")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEdit ? "Editar Empresa" : "Nova Empresa"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? "Edite os dados da empresa" : "Cadastre uma nova empresa"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="dados-basicos" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
            <TabsTrigger value="endereco">Endereço</TabsTrigger>
            <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
            <TabsTrigger value="contatos">Contatos</TabsTrigger>
            <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
          </TabsList>

          <TabsContent value="dados-basicos">
            <Card>
              <CardHeader>
                <CardTitle>Dados Básicos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="codigo_empresa">Código Empresa</Label>
                    <Input
                      id="codigo_empresa"
                      value={formData.codigo_empresa || ""}
                      disabled
                      placeholder="Será gerado automaticamente"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cnpj">CNPJ *</Label>
                    <Input
                      id="cnpj"
                      value={formData.cnpj}
                      onChange={(e) => handleInputChange("cnpj", e.target.value)}
                      placeholder="00.000.000/0000-00"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="razao_social">Razão Social *</Label>
                  <Input
                    id="razao_social"
                    value={formData.razao_social}
                    onChange={(e) => handleInputChange("razao_social", e.target.value)}
                    placeholder="Razão social da empresa"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="apelido">Apelido</Label>
                  <Input
                    id="apelido"
                    value={formData.apelido || ""}
                    onChange={(e) => handleInputChange("apelido", e.target.value)}
                    placeholder="Nome fantasia ou apelido"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="ativo"
                    checked={formData.ativo}
                    onCheckedChange={(checked) => handleInputChange("ativo", checked)}
                  />
                  <Label htmlFor="ativo">Empresa ativa</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="endereco">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Endereços (Locais)</CardTitle>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetLocalForm();
                    setIsLocalModalOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Local
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Locais Associados</Label>
                  <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                    {locais.map((local) => (
                      <div key={local.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`local-${local.id}`}
                          checked={selectedLocais.includes(local.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedLocais([...selectedLocais, local.id]);
                            } else {
                              setSelectedLocais(selectedLocais.filter(id => id !== local.id));
                            }
                          }}
                        />
                        <Label htmlFor={`local-${local.id}`} className="flex-1">
                          {local.nome} - {local.logradouro}, {local.cidade}/{local.estado}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fiscal">
            <Card>
              <CardHeader>
                <CardTitle>Dados Fiscais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inscricao_estadual">Inscrição Estadual (IE)</Label>
                    <Input
                      id="inscricao_estadual"
                      value={formData.inscricao_estadual || ""}
                      onChange={(e) => handleInputChange("inscricao_estadual", e.target.value)}
                      placeholder="IE da empresa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inscricao_municipal">Inscrição Municipal (IM)</Label>
                    <Input
                      id="inscricao_municipal"
                      value={formData.inscricao_municipal || ""}
                      onChange={(e) => handleInputChange("inscricao_municipal", e.target.value)}
                      placeholder="IM da empresa"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_nfe">E-mail NFE</Label>
                  <Input
                    id="email_nfe"
                    type="email"
                    value={formData.email_nfe || ""}
                    onChange={(e) => handleInputChange("email_nfe", e.target.value)}
                    placeholder="email@empresa.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="fora_municipio"
                      checked={formData.fora_municipio}
                      onCheckedChange={(checked) => handleInputChange("fora_municipio", checked)}
                    />
                    <Label htmlFor="fora_municipio">Fora do Município?</Label>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ibge_municipio">IBGE Município</Label>
                    <Input
                      id="ibge_municipio"
                      value={formData.ibge_municipio || ""}
                      onChange={(e) => handleInputChange("ibge_municipio", e.target.value)}
                      placeholder="Código IBGE do município"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contatos">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Contatos Associados</CardTitle>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetContatoForm();
                    setIsContatoModalOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Contato
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Lista de Contatos</Label>
                  <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                    {contatos.map((contato) => (
                      <div key={contato.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`contato-${contato.id}`}
                          checked={selectedContatos.includes(contato.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedContatos([...selectedContatos, contato.id]);
                            } else {
                              setSelectedContatos(selectedContatos.filter(id => id !== contato.id));
                            }
                          }}
                        />
                        <Label htmlFor={`contato-${contato.id}`} className="flex-1">
                          {contato.nome} - {contato.cargo} - {contato.email} - {contato.telefone}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="financeiro">
            <Card>
              <CardHeader>
                <CardTitle>Dados Financeiros</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="limite_credito">Limite de Crédito</Label>
                    <Input
                      id="limite_credito"
                      type="number"
                      step="0.01"
                      value={formData.limite_credito || ""}
                      onChange={(e) => handleInputChange("limite_credito", parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="matricula_cei_cno">Matrícula CEI/CNO</Label>
                    <Input
                      id="matricula_cei_cno"
                      value={formData.matricula_cei_cno || ""}
                      onChange={(e) => handleInputChange("matricula_cei_cno", e.target.value)}
                      placeholder="Matrícula CEI/CNO"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Dados Bancários de Recebimento</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="banco">Banco</Label>
                      <Input
                        id="banco"
                        value={formData.banco || ""}
                        onChange={(e) => handleInputChange("banco", e.target.value)}
                        placeholder="Nome do banco"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="agencia">Agência</Label>
                      <Input
                        id="agencia"
                        value={formData.agencia || ""}
                        onChange={(e) => handleInputChange("agencia", e.target.value)}
                        placeholder="Número da agência"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="conta">Conta</Label>
                      <Input
                        id="conta"
                        value={formData.conta || ""}
                        onChange={(e) => handleInputChange("conta", e.target.value)}
                        placeholder="Número da conta"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pix">Pix</Label>
                      <Input
                        id="pix"
                        value={formData.pix || ""}
                        onChange={(e) => handleInputChange("pix", e.target.value)}
                        placeholder="Chave Pix"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cnpj_bancario">CNPJ</Label>
                      <Input
                        id="cnpj_bancario"
                        value={formData.cnpj_bancario || ""}
                        onChange={(e) => handleInputChange("cnpj_bancario", e.target.value)}
                        placeholder="CNPJ para dados bancários"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/intranet/empresas")}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </Tabs>
      </form>

      {/* Modal de Novo Contato */}
      <Dialog open={isContatoModalOpen} onOpenChange={setIsContatoModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Contato</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContatoSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contato-nome">Nome *</Label>
                <Input
                  id="contato-nome"
                  value={contatoFormData.nome}
                  onChange={(e) => setContatoFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="contato-email">E-mail</Label>
                <Input
                  id="contato-email"
                  type="email"
                  value={contatoFormData.email}
                  onChange={(e) => setContatoFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contato-telefone">Telefone</Label>
                <Input
                  id="contato-telefone"
                  value={contatoFormData.telefone}
                  onChange={(e) => setContatoFormData(prev => ({ ...prev, telefone: e.target.value }))}
                />
              </div>
              
              <div>
                <Label htmlFor="contato-cargo">Cargo</Label>
                <Input
                  id="contato-cargo"
                  value={contatoFormData.cargo}
                  onChange={(e) => setContatoFormData(prev => ({ ...prev, cargo: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contato-cliente_id">Cliente (opcional)</Label>
                <Select 
                  value={contatoFormData.cliente_id} 
                  onValueChange={(value) => setContatoFormData(prev => ({ ...prev, cliente_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um cliente (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum cliente</SelectItem>
                    {clientes.map((cliente) => (
                      <SelectItem key={cliente.id} value={cliente.id}>
                        {cliente.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="contato-local_id">Local (opcional)</Label>
                <Select 
                  value={contatoFormData.local_id} 
                  onValueChange={(value) => setContatoFormData(prev => ({ ...prev, local_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um local (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {locais.map((local) => (
                      <SelectItem key={local.id} value={local.id}>
                        {local.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="contato-observacoes">Observações</Label>
              <Textarea
                id="contato-observacoes"
                value={contatoFormData.observacoes}
                onChange={(e) => setContatoFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsContatoModalOpen(false);
                resetContatoForm();
              }}>
                Cancelar
              </Button>
              <Button type="submit" disabled={contatoLoading}>
                {contatoLoading ? "Salvando..." : "Criar Contato"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Novo Local */}
      <Dialog open={isLocalModalOpen} onOpenChange={setIsLocalModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Criar Novo Local</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLocalSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="local-nome">Nome *</Label>
                <Input
                  id="local-nome"
                  value={localFormData.nome}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, nome: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="local-tipo_local_id">Tipo do Local *</Label>
                <Select 
                  value={localFormData.tipo_local_id} 
                  onValueChange={(value) => setLocalFormData(prev => ({ ...prev, tipo_local_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo do local" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposLocal.map((tipo) => (
                      <SelectItem key={tipo.id} value={tipo.id}>
                        {tipo.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="local-descricao">Descrição</Label>
              <Textarea
                id="local-descricao"
                value={localFormData.descricao}
                onChange={(e) => setLocalFormData(prev => ({ ...prev, descricao: e.target.value }))}
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="local-cliente_id">Cliente (opcional)</Label>
              <Select 
                value={localFormData.cliente_id} 
                onValueChange={(value) => setLocalFormData(prev => ({ ...prev, cliente_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um cliente (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum cliente</SelectItem>
                  {clientes.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="local-cep">CEP</Label>
                <Input
                  id="local-cep"
                  value={localFormData.cep}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, cep: e.target.value }))}
                  placeholder="00000-000"
                />
              </div>

              <div>
                <Label htmlFor="local-logradouro">Logradouro</Label>
                <Input
                  id="local-logradouro"
                  value={localFormData.logradouro}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, logradouro: e.target.value }))}
                  placeholder="Rua, Avenida, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="local-numero">Número</Label>
                <Input
                  id="local-numero"
                  value={localFormData.numero}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, numero: e.target.value }))}
                  placeholder="123"
                />
              </div>

              <div>
                <Label htmlFor="local-complemento">Complemento</Label>
                <Input
                  id="local-complemento"
                  value={localFormData.complemento}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, complemento: e.target.value }))}
                  placeholder="Apto, Sala, etc."
                />
              </div>

              <div>
                <Label htmlFor="local-bairro">Bairro</Label>
                <Input
                  id="local-bairro"
                  value={localFormData.bairro}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, bairro: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="local-cidade">Cidade</Label>
                <Input
                  id="local-cidade"
                  value={localFormData.cidade}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, cidade: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="local-estado">Estado</Label>
                <Input
                  id="local-estado"
                  value={localFormData.estado}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, estado: e.target.value }))}
                  placeholder="SP"
                  maxLength={2}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="local-frete_moto">Frete Moto (R$)</Label>
                <Input
                  id="local-frete_moto"
                  type="number"
                  step="0.01"
                  value={localFormData.frete_moto}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, frete_moto: e.target.value }))}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="local-frete_carro_hatch">Frete Carro Hatch (R$)</Label>
                <Input
                  id="local-frete_carro_hatch"
                  type="number"
                  step="0.01"
                  value={localFormData.frete_carro_hatch}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, frete_carro_hatch: e.target.value }))}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="local-frete_utilitario">Frete Utilitário (R$)</Label>
                <Input
                  id="local-frete_utilitario"
                  type="number"
                  step="0.01"
                  value={localFormData.frete_utilitario}
                  onChange={(e) => setLocalFormData(prev => ({ ...prev, frete_utilitario: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setIsLocalModalOpen(false);
                resetLocalForm();
              }}>
                Cancelar
              </Button>
              <Button type="submit" disabled={localLoading}>
                {localLoading ? "Salvando..." : "Criar Local"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}